import { useEffect, useMemo, useState } from "react";
import {
  buildOwnerLabelByUserId,
  shouldShowLeadsManagerFilters,
  shouldShowLeadsOwnerFilter,
} from "@/lib/lead/leadsManagerFilters";
import {
  fetchLeadTransferCandidates,
  type LeadTransferCandidate,
} from "@/lib/lead/transferLeadOwner";

type LocationOption = {
  id: string;
  name?: string;
  fullName?: string;
  city?: string;
};

function locationOptionLabel(loc: LocationOption): string {
  return loc.fullName || loc.name || loc.city || loc.id;
}

export function useLeadsManagerFilterOptions(args: {
  organizationId: string | null | undefined;
  viewOrgWideLeads: boolean;
  selectableLocations: LocationOption[];
}) {
  const { organizationId, viewOrgWideLeads, selectableLocations } = args;
  const [recentLocationFilter, setRecentLocationFilter] = useState<string | "all">("all");
  const [recentOwnerFilter, setRecentOwnerFilter] = useState<string | "all">("all");
  const [ownerCandidates, setOwnerCandidates] = useState<LeadTransferCandidate[]>([]);

  const showLeadsManagerFilters = shouldShowLeadsManagerFilters({
    viewOrgWide: viewOrgWideLeads,
    selectableLocationCount: selectableLocations.length,
  });
  const showLeadsOwnerFilter = shouldShowLeadsOwnerFilter(viewOrgWideLeads);

  useEffect(() => {
    if (!showLeadsOwnerFilter || !organizationId) {
      setOwnerCandidates([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data } = await fetchLeadTransferCandidates(organizationId);
      if (!cancelled) setOwnerCandidates(data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, showLeadsOwnerFilter]);

  const ownerLabelByUserId = useMemo(
    () => buildOwnerLabelByUserId(ownerCandidates),
    [ownerCandidates]
  );

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const loc of selectableLocations) {
      if (loc.id) map.set(loc.id, locationOptionLabel(loc));
    }
    return map;
  }, [selectableLocations]);

  const locationFilterOptions = useMemo(
    () =>
      selectableLocations.map((loc) => ({
        id: loc.id,
        label: locationOptionLabel(loc),
      })),
    [selectableLocations]
  );

  const managerListFilters = useMemo(() => {
    if (!showLeadsManagerFilters) return undefined;
    return {
      locationId: recentLocationFilter,
      ownerUserId: recentOwnerFilter,
    };
  }, [recentLocationFilter, recentOwnerFilter, showLeadsManagerFilters]);

  return {
    showLeadsManagerFilters,
    showLeadsOwnerFilter,
    recentLocationFilter,
    setRecentLocationFilter,
    recentOwnerFilter,
    setRecentOwnerFilter,
    ownerCandidates,
    ownerLabelByUserId,
    locationNameById,
    locationFilterOptions,
    managerListFilters,
  };
}
